import { withFormik } from 'formik';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import { LoginFormProps, FormValues } from './types';
import { iRootDispatch } from '../../store';
import * as Yup from 'yup';
import LoginForm from './LoginForm';
import {
  dangerNotification,
  successNotification,
  warningNotification
} from '../../services/notification';
import { CurrentPageState, CurrentToggledMenuState } from '../../store/types/temp-types';
import { UserState } from '../../types/UserState';
import {
  oAuthSignIn,
  redirectToCapturePage,
  redirectToOAuthLogin,
  checkUserExists,
  verifyExternalLogin,
  confiaxSignIn,
  forgotPassword
} from '../../services/oauth';
import { useState } from 'react';
import React from 'react';
import * as Storage from '../../services/storage';
import { ResponsibleCompanyEnum } from '../../enums/responsible-company.enum';

const mapDispatch = (dispatch: iRootDispatch) => ({
  updateUser: (user: UserState) => dispatch.user.updateUser(user),
  updateCurrentPage: (currentPage: CurrentPageState) =>
    dispatch.currentPage.updateCurrentPage(currentPage),
  updateCurrentToggledMenu: (state: CurrentToggledMenuState) =>
    dispatch.currentToggledMenu.updateCurrentToggledMenu(state)
});

export type LoginProps = ReturnType<typeof mapDispatch> &
  RouteComponentProps &
  LoginFormProps;

type LoginStateProps = {
  isInternalLogin: boolean;
  setIsInternalLogin: (value: boolean) => void;
  hasPassword: boolean;
  setHasPassword: (value: boolean) => void;
};

type LoginFormikProps = LoginProps & LoginStateProps;

const LoginFormWithFormik = withFormik<LoginFormikProps, FormValues>({
  mapPropsToValues: loginFormProps => ({
    email: loginFormProps.initialEmail || loginFormProps.hasEmail || '',
    password: loginFormProps.initialPassword || ''
  }),

  validationSchema: Yup.object().shape({
    email: Yup.string()
      .email('O e-mail informado não é válido')
      .required('Favor informar o e-mail')
  }),

  async handleSubmit({ email, password }: FormValues, { props, setSubmitting }) {
    const { isInternalLogin, setIsInternalLogin, setHasPassword } = props;

    try {
      const normalizedEmail = email.toLowerCase();
      const verify = await verifyExternalLogin(normalizedEmail);
      const isConfiaxClient = verify.company === ResponsibleCompanyEnum.confiax;

      if (verify.company) {
        const isRv = process.env.REACT_APP_IS_RV === 'true';
        const targetCompany = isRv
          ? ResponsibleCompanyEnum.vistorias
          : ResponsibleCompanyEnum.confiax;
        const url =
          verify.company === ResponsibleCompanyEnum.vistorias
            ? process.env.REACT_APP_RV_URL
            : process.env.REACT_APP_CONFIAX_URL;

        if (verify.company !== targetCompany) {
          warningNotification(
            'Atenção!',
            'Vamos te redirecionar para sua nova plataforma!'
          );
          if (isConfiaxClient) {
            redirectToCapturePage();
            return;
          }
          if (url) {
            warningNotification('Atenção!', 'Este usuário não pertence a esta empresa!');
            setTimeout(() => {
              window.location.href = url;
            }, 1000);
          }
          return;
        }
      }

      if (verify) {
        const external = verify.isExternal ? 'true' : 'false';
        Storage.set('external_login', external);
      }

      if ((!password || password.length === 0) && !isInternalLogin) {
        if (isConfiaxClient) {
          redirectToCapturePage();
          return;
        }

        if (verify.isExternal) {
          oAuthSignIn(normalizedEmail)
            .then(async response => {
              if (response) {
                redirectToOAuthLogin(normalizedEmail);
              } else {
                const userExists = await checkUserExists(normalizedEmail);
                if (!userExists.isValid) {
                  switch (userExists.message) {
                    case 'User was not migrated to OAuth RV':
                      warningNotification(
                        'Oops...',
                        'Você ainda não está cadastrado no Grupo RV!'
                      );
                      props.history.push(`/login#email=${normalizedEmail}`);
                      break;
                    case 'User not found':
                      warningNotification('Oops...', 'Usuário não encontrado!');
                      setSubmitting(false);
                      break;
                    case 'This user is not from this company':
                      warningNotification(
                        'Oops...',
                        'Este usuário não pertence a esta empresa!'
                      );
                      setSubmitting(false);
                      break;
                    case 'User is not active':
                      warningNotification('Oops...', 'Usuário inativo!');
                      setSubmitting(false);
                      break;
                  }
                } else {
                  if (userExists.message === 'User was migrated to OAuth RV') {
                    redirectToOAuthLogin(normalizedEmail);
                    return;
                  }

                  dangerNotification(
                    'Oops...',
                    'Ocorreu um erro na tentativa de login, por favor contate o suporte!'
                  );
                  setSubmitting(false);
                }
              }
            })
            .catch(() => {
              redirectToCapturePage();
            });
        } else {
          if (!verify.hasPassword) {
            await forgotPassword(normalizedEmail);
            successNotification(
              'Sucesso!',
              'Um link para redefinição da senha foi enviado para o e-mail informado'
            );
          }
          setHasPassword(verify.hasPassword);
          setIsInternalLogin(true);
          setSubmitting(false);
        }
      } else if (password.length > 0) {
        const response = await confiaxSignIn(normalizedEmail, password);
        if (response) {
          redirectToCapturePage();
        } else {
          setSubmitting(false);
        }
      } else {
        setSubmitting(false);
      }
    } catch (error) {
      const message =
        (error as { message?: string })?.message ||
        (error instanceof Error ? error.message : undefined) ||
        'Não foi possível realizar o login.';
      dangerNotification('Oops...', message);
      setSubmitting(false);
    }
  }
})(LoginForm);

const Login = (props: LoginProps) => {
  const [isInternalLogin, setIsInternalLogin] = useState<boolean>(false);
  const [hasPassword, setHasPassword] = useState<boolean>(false);

  return (
    <LoginFormWithFormik
      {...props}
      isInternalLogin={isInternalLogin}
      setIsInternalLogin={setIsInternalLogin}
      hasPassword={hasPassword}
      setHasPassword={setHasPassword}
    />
  );
};

export default connect(null, mapDispatch)(Login);
